import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'

type AddInput = {
  itemId:       string
  itemName:     string
  householdId:  string
  startNewList: boolean   // create a list if no active one exists
}

export type AddToListResult = {
  listId:        string
  alreadyOnList: boolean
  startedNew:    boolean
}

// Adds a pantry item to the household's active shopping list, optionally
// creating a new list if none is active. Idempotent: detects an existing
// (non-removed) link and returns alreadyOnList=true rather than duplicating.
export function useAddItemToActiveList() {
  const qc = useQueryClient()

  return useMutation<AddToListResult, Error, AddInput>({
    mutationFn: async (input) => {
      // Find the most recent in-flight list. unpacked_at IS NULL is the
      // newer dedicated marker; completed_at IS NULL is kept in lockstep
      // by useFinishUnpack so requiring both is safe and stricter.
      const { data: activeLists, error: listErr } = await supabase
        .from('shopping_lists')
        .select('id')
        .eq('household_id', input.householdId)
        .is('completed_at', null)
        .is('unpacked_at',  null)
        .order('generated_at', { ascending: false })
        .limit(1)
      if (listErr) throw listErr

      let listId  = activeLists?.[0]?.id ?? null
      let started = false

      if (!listId && input.startNewList) {
        const { data: newList, error: newListErr } = await supabase
          .from('shopping_lists')
          .insert({
            household_id: input.householdId,
            generated_at: new Date().toISOString(),
          })
          .select('id')
          .single()
        if (newListErr || !newList) throw newListErr ?? new Error('Could not start list')
        listId = newList.id
        started = true
      }

      if (!listId) throw new Error('No active shopping list')

      // Idempotency: don't duplicate a link that already exists.
      const { data: existingLink } = await supabase
        .from('list_items')
        .select('id, status')
        .eq('list_id', listId)
        .eq('item_id', input.itemId)
        .neq('status', 'removed')
        .limit(1)
        .maybeSingle()

      if (existingLink) {
        return { listId, alreadyOnList: true, startedNew: started }
      }

      const { error: insErr } = await supabase
        .from('list_items')
        .insert({
          list_id:       listId,
          item_id:       input.itemId,
          status:        'pending',
          urgency_score: 80,    // out-of-stock items rank near the top
          sort_order:    0,
          is_one_off:    false,
        })
      if (insErr) throw insErr

      return { listId, alreadyOnList: false, startedNew: started }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['shopping-lists', vars.householdId] })
      qc.invalidateQueries({ queryKey: ['list-items'] })
      qc.invalidateQueries({ queryKey: ['active-list',  vars.householdId] })
      qc.invalidateQueries({ queryKey: ['latestList',   vars.householdId] })
    },
  })
}
